import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  bindTooltipEvents,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const MonthlyOutageForecastBarGraph = ({ data, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    // Calculate dimensions based on the size of the parent container
    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 60, right: 10, bottom: 50, left: 60 }; // Increased top margin for legend

    // Set dimensions considering margins
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    // Create SVG
    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Define energy categories (excluding dateAdded and marketDate)
    const friendlyNames = {
      biomassAndOther: "Biomass & Other",
      cc: "Combined Cycle",
      //coal: "Coal",
      cogen: "Cogeneration",
      //dualFuel: "Dual Fuel",
      energyStorage: "Energy Storage",
      gfs: "Gas-Fired Steam",
      hydro: "Hydro",
      mbo: "Misc. Base Load",
      sc: "Simple Cycle",
      solar: "Solar",
      wind: "Wind",
    };

    const energyCategories = [
      "biomassAndOther",
      "cc",
      //"coal",
      "cogen",
      //"dualFuel",
      "energyStorage",
      "gfs",
      "hydro",
      "mbo",
      "sc",
      "solar",
      "wind",
    ];

    // Stack the data
    const stack = d3
      .stack()
      .keys(energyCategories)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const stackedData = stack(data);

    // Set up scales
    const parseDate = d3.timeParse("%b %Y");
    const sortedMonths = data
      .map((d) => d.month)
      .sort((a, b) => parseDate(a) - parseDate(b));

    const xScale = d3
      .scaleBand()
      .domain(sortedMonths)
      .range([0, innerWidth])
      .padding(0.1);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(stackedData, (d) => d3.max(d, (d) => d[1]))])
      .nice()
      .range([innerHeight, 0]);

    // Add gridlines
    chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    // Color scale for different energy categories
    const colorScale = d3
      .scaleOrdinal(d3.schemePaired)
      .domain(energyCategories);
    const stackCornerRadius = 3;
    const stackEdgesByMonth = new Map(
      sortedMonths.map((month, monthIndex) => {
        const activeSeries = stackedData.filter((series) => {
          const segment = series[monthIndex];
          return segment && segment[1] - segment[0] > 0;
        });

        return [
          month,
          {
            bottomKey: activeSeries[0]?.key,
            topKey: activeSeries[activeSeries.length - 1]?.key,
          },
        ];
      }),
    );

    const buildStackSegmentPath = (
      x,
      y,
      width,
      height,
      radius,
      roundTop,
      roundBottom,
    ) => {
      if (
        x === undefined ||
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        !Number.isFinite(width) ||
        !Number.isFinite(height) ||
        height <= 0 ||
        width <= 0
      ) {
        return "";
      }

      const topRadius = roundTop ? Math.min(radius, width / 2, height / 2) : 0;
      const bottomRadius = roundBottom
        ? Math.min(radius, width / 2, height / 2)
        : 0;
      const right = x + width;
      const bottom = y + height;

      return [
        `M${x},${bottom - bottomRadius}`,
        bottomRadius > 0
          ? `Q${x},${bottom} ${x + bottomRadius},${bottom}`
          : `L${x},${bottom}`,
        `L${right - bottomRadius},${bottom}`,
        bottomRadius > 0
          ? `Q${right},${bottom} ${right},${bottom - bottomRadius}`
          : `L${right},${bottom}`,
        `L${right},${y + topRadius}`,
        topRadius > 0
          ? `Q${right},${y} ${right - topRadius},${y}`
          : `L${right},${y}`,
        `L${x + topRadius},${y}`,
        topRadius > 0 ? `Q${x},${y} ${x},${y + topRadius}` : `L${x},${y}`,
        "Z",
      ].join(" ");
    };

    // Create and add the bar groups
    const barGroups = chartSvg
      .selectAll(".bar-group")
      .data(stackedData)
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("fill", (d) => colorScale(d.key));

    // Create and add the bars
    const stackSegments = barGroups
      .selectAll("path")
      .data((series) =>
        series.map((segment) => ({
          key: series.key,
          month: segment.data.month,
          start: segment[0],
          end: segment[1],
        })),
      )
      .enter()
      .append("path")
      .attr("class", "bar dashboard-bar dashboard-stacked-bar")
      .attr("d", (d) => {
        const stackEdges = stackEdgesByMonth.get(d.month) || {};
        return buildStackSegmentPath(
          xScale(d.month),
          yScale(d.end),
          xScale.bandwidth(),
          yScale(d.start) - yScale(d.end),
          stackCornerRadius,
          d.key === stackEdges.topKey,
          d.key === stackEdges.bottomKey,
        );
      });
    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    bindTooltipEvents({
      selection: stackSegments,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: d.month,
          rows: [
            {
              label: friendlyNames[d.key] || d.key,
              value: `${formatTooltipNumber(d.end - d.start, {
                maximumFractionDigits: 0,
              })} MW`,
              color: colorScale(d.key),
            },
          ],
        }),
    });

    // Add X axis
    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .attr("class", "d3-chart-axis");

    // Add Y axis
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale))
      .attr("class", "d3-chart-axis");

    // Add title
    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("AESO Monthly Outage Forecast")
      .attr("class", "d3-chart-title");

    // Add Y axis label
    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Energy (MW)")
      .attr("class", "d3-chart-axis-label");

    // Add legend
    const legendItemsPerRow = Math.ceil(energyCategories.length / 2);
    const legendSpacing = innerWidth / legendItemsPerRow;
    const legendBlockSize = 10;
    const legendTextOffset = 5; // Space between rectangle and text
    const legendRowHeight = 20; // Height between rows

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "start")
      .attr("transform", `translate(0, ${-margin.top / 2 + 5})`)
      .selectAll("g")
      .data(energyCategories)
      .enter()
      .append("g")
      .attr(
        "transform",
        (d, i) =>
          `translate(${(i % legendItemsPerRow) * legendSpacing}, ${
            Math.floor(i / legendItemsPerRow) * legendRowHeight
          })`,
      )
      .attr("fill", "grey");

    legend
      .append("rect")
      .attr("width", legendBlockSize)
      .attr("height", legendBlockSize)
      .attr("fill", colorScale);

    legend
      .append("text")
      .attr("x", legendBlockSize + legendTextOffset)
      .attr("y", legendBlockSize / 2)
      .attr("dy", "0.35em")
      .text((d) => friendlyNames[d] || d)
      .attr("class", "d3-chart-legend-label");
  }, [data]);

  useEffect(() => {
    !isLoading && renderChart();
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}></svg>;
};

export default MonthlyOutageForecastBarGraph;
















